import React, { useState, useMemo } from 'react';
import { formatDateForDisplay } from '../../../utils/calendarUtils';
import { getDaysSinceOrder, getOrderRowBackgroundColor } from '../../../utils/orderUtils';

interface OrderDetail {
  customerName: string;
  batches: number;
  orderDate: string;
  orderId: string;
}

interface OrderConfirmationModalProps {
  isOpen: boolean;
  fillingName: string;
  totalBatches: number;
  orderDetails: OrderDetail[];
  onConfirm: (selectedOrderIds: string[]) => void;
  onCancel: () => void;
  unitType?: 'batches' | 'units'; // Optional, defaults to 'batches'
}

const OrderConfirmationModal: React.FC<OrderConfirmationModalProps> = ({
  isOpen,
  fillingName,
  totalBatches,
  orderDetails,
  onConfirm,
  onCancel,
  unitType = 'batches' // Default to batches for backward compatibility
}) => {
  // State to track which orders are selected (by orderId)
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [showWarning, setShowWarning] = useState(false);

  // Calculate total batches from selected orders
  const selectedBatches = useMemo(() => {
    return orderDetails
      .filter(order => selectedOrders.has(order.orderId))
      .reduce((sum, order) => sum + order.batches, 0);
  }, [selectedOrders, orderDetails]);

  // Toggle selection for a specific order
  const handleOrderClick = (orderId: string) => {
    setSelectedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
    setShowWarning(false); // Clear warning when user interacts
  };

  // Toggle all orders
  const handleToggleAll = () => {
    if (selectedOrders.size === orderDetails.length) {
      // Deselect all
      setSelectedOrders(new Set());
    } else {
      // Select all
      setSelectedOrders(new Set(orderDetails.map(order => order.orderId)));
    }
    setShowWarning(false); // Clear warning when user interacts
  };

  // Handle confirm button click
  const handleConfirmClick = () => {
    if (selectedOrders.size === 0) {
      setShowWarning(true);
      return;
    }
    onConfirm(Array.from(selectedOrders));
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .confirm-button:hover {
          background-color: #1976D2;
        }
        .cancel-button:hover {
          background-color: #f5f5f5;
        }
        .toggle-all-button:hover {
          background-color: #E3F2FD;
        }
      `}</style>
      <div style={styles.overlay} onClick={onCancel}>
        <div style={styles.modal} onClick={e => e.stopPropagation()}>
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>
              {unitType === 'units' ? 'Confirm Order Fulfillment Task' : 'Confirm Filling Prep Task'}
            </h2>
            <button style={styles.closeButton} onClick={onCancel}>
              ×
            </button>
          </div>

          <div style={styles.content}>
            <div style={styles.taskSummary}>
              <p style={styles.summaryLabel}>You are scheduling:</p>
              <p style={styles.taskDescription}>
                prep {selectedBatches > 0 ? selectedBatches : totalBatches}{' '}
                {unitType === 'batches'
                  ? `batch${(selectedBatches > 0 ? selectedBatches : totalBatches) === 1 ? '' : 'es'}`
                  : `unit${(selectedBatches > 0 ? selectedBatches : totalBatches) === 1 ? '' : 's'}`}{' '}
                of <strong>{fillingName}</strong>
              </p>
              {selectedOrders.size > 0 && selectedOrders.size < orderDetails.length && (
                <p style={styles.partialSelectionNote}>
                  ({selectedOrders.size} of {orderDetails.length} orders selected)
                </p>
              )}
            </div>

            {orderDetails.length > 0 ? (
              <div style={styles.ordersSection}>
                <div style={styles.ordersSectionHeader}>
                  <h3 style={styles.sectionTitle}>This filling is needed for:</h3>
                  <button className="toggle-all-button" style={styles.toggleAllButton} onClick={handleToggleAll}>
                    {selectedOrders.size === orderDetails.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div style={styles.ordersList}>
                  {orderDetails.map((order, index) => {
                    const isSelected = selectedOrders.has(order.orderId);
                    const daysSince = getDaysSinceOrder(order.orderDate);
                    const ageBackgroundColor = getOrderRowBackgroundColor(daysSince, '');

                    return (
                      <div
                        key={order.orderId}
                        style={{
                          ...styles.orderItem,
                          backgroundColor: isSelected ? '#E3F2FD' : ageBackgroundColor,
                          border: isSelected ? '2px solid #2196F3' : '1px solid #e0e0e0',
                          boxShadow: isSelected ? '0 2px 4px rgba(33, 150, 243, 0.2)' : 'none'
                        }}
                        onClick={() => handleOrderClick(order.orderId)}
                      >
                        <div style={styles.orderHeader}>
                          <span style={styles.customerName}>{order.customerName}</span>
                          <span
                            style={{
                              ...styles.batchCount,
                              ...(isSelected ? styles.batchCountSelected : {})
                            }}
                          >
                            {order.batches}{' '}
                            {unitType === 'batches'
                              ? `batch${order.batches === 1 ? '' : 'es'}`
                              : `unit${order.batches === 1 ? '' : 's'}`}
                          </span>
                        </div>
                        <div style={styles.orderDate}>
                          Order Date: {formatDateForDisplay(order.orderDate)}
                          {isSelected && (
                            <span style={styles.ageBadge}>
                              {daysSince} day{daysSince !== 1 ? 's' : ''} old
                            </span>
                          )}
                        </div>
                        {isSelected && <div style={styles.checkmark}>✓</div>}
                      </div>
                    );
                  })}
                </div>
                {showWarning && <div style={styles.warningMessage}>Please select at least one order to continue</div>}
              </div>
            ) : (
              <div style={styles.noOrdersMessage}>
                <p style={styles.noOrdersText}>No active orders found for this filling.</p>
                <p style={styles.noOrdersSubtext}>You can still schedule this task for prep planning.</p>
              </div>
            )}

            <div style={styles.actions}>
              <button className="cancel-button" style={styles.cancelButton} onClick={onCancel}>
                Cancel
              </button>
              <button className="confirm-button" style={styles.confirmButton} onClick={handleConfirmClick}>
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e0e0e0'
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#333'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    color: '#999',
    cursor: 'pointer',
    padding: 0,
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  content: {
    padding: '20px'
  },
  taskSummary: {
    marginBottom: '24px',
    padding: '16px',
    backgroundColor: '#E3F2FD',
    borderRadius: '6px',
    borderLeft: '4px solid #2196F3'
  },
  summaryLabel: {
    margin: '0 0 8px 0',
    fontSize: '14px',
    color: '#666',
    fontWeight: '500'
  },
  taskDescription: {
    margin: 0,
    fontSize: '16px',
    color: '#333'
  },
  ordersSection: {
    marginBottom: '24px'
  },
  ordersSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px'
  },
  sectionTitle: {
    margin: 0,
    fontSize: '14px',
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  toggleAllButton: {
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#2196F3',
    backgroundColor: 'white',
    border: '1px solid #2196F3',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  ordersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  orderItem: {
    position: 'relative',
    padding: '12px',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  orderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px'
  },
  customerName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333'
  },
  batchCount: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#2196F3'
  },
  batchCountSelected: {
    color: '#1976D2'
  },
  orderDate: {
    fontSize: '12px',
    color: '#666',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px'
  },
  checkmark: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    width: '24px',
    height: '24px',
    backgroundColor: '#2196F3',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold'
  },
  ageBadge: {
    padding: '4px 8px',
    borderRadius: '4px',
    backgroundColor: '#2196F3',
    color: 'white',
    fontSize: '11px',
    fontWeight: '600'
  },
  partialSelectionNote: {
    margin: '8px 0 0 0',
    fontSize: '13px',
    color: '#666',
    fontStyle: 'italic'
  },
  warningMessage: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: '#FFF3E0',
    border: '1px solid #FFB74D',
    borderRadius: '6px',
    color: '#E65100',
    fontSize: '13px',
    fontWeight: '500',
    textAlign: 'center'
  },
  noOrdersMessage: {
    textAlign: 'center',
    padding: '24px',
    backgroundColor: '#FFF3E0',
    borderRadius: '6px',
    marginBottom: '24px'
  },
  noOrdersText: {
    margin: '0 0 8px 0',
    fontSize: '14px',
    color: '#333',
    fontWeight: '500'
  },
  noOrdersSubtext: {
    fontSize: '13px',
    color: '#666',
    margin: 0
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px'
  },
  cancelButton: {
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#666',
    backgroundColor: 'white',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  confirmButton: {
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
    backgroundColor: '#2196F3',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  }
};

export default OrderConfirmationModal;
